import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import "../cotizaciones.css";
import axios from "axios";

const API_BASE = "https://api-rci-g5ebbhava3anbgfy.centralus-01.azurewebsites.net";

// Axios con token (si existe)
const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function Cotizaciones() {
  const location = useLocation();

  // ====== clientes ======
  const [clientes, setClientes] = useState([]);
  const [busquedaCliente, setBusquedaCliente] = useState("");
  const [clienteSeleccionadoId, setClienteSeleccionadoId] = useState("");
  const [clienteSeleccionadoNombre, setClienteSeleccionadoNombre] = useState("");

  // dropdown autocomplete
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const wrapperRef = useRef(null);

  // panel nuevo cliente
  const [mostrarNuevoCliente, setMostrarNuevoCliente] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState({
    Nombre: "",
    Correo: "",
    RFC: "",
  });

  // ====== cotización ======
  const [incluyeMaterial, setIncluyeMaterial] = useState(true);
  const [incluyeManoObra, setIncluyeManoObra] = useState(true);

  const [servicios, setServicios] = useState([
    { cantidad: "", descripcion: "", precioUnitario: "" },
  ]);

  // lista de cotizaciones
  const [cotizaciones, setCotizaciones] = useState([]);
  const [filtroTabla, setFiltroTabla] = useState("");

  // para habilitar export PDF
  const [cotizacionGuardadaId, setCotizacionGuardadaId] = useState(null);

  // ====== carga inicial ======
  useEffect(() => {
    cargarClientes();
    cargarCotizaciones();
  }, []);

  // cerrar sugerencias si das click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setMostrarSugerencias(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const cargarClientes = async () => {
    try {
      const res = await api.get("/api/Clientes");
      setClientes(res.data || []);
    } catch (err) {
      console.error("Error al obtener clientes:", err);
    }
  };

  const cargarCotizaciones = async () => {
    try {
      const res = await api.get("/api/Cotizaciones");
      setCotizaciones(res.data || []);
    } catch (err) {
      console.error("Error al obtener cotizaciones:", err);
    }
  };

  // ====== filtro clientes (buscador) ======
  const clientesFiltrados = useMemo(() => {
    const q = busquedaCliente.trim().toLowerCase();
    if (!q) return clientes;

    return clientes.filter((c) => {
      const nombre = (c.nombre || c.Nombre || "").toLowerCase();
      const correo = (c.correo || c.Correo || "").toLowerCase();
      const tel = (c.telefono || c.Telefono || "").toLowerCase();
      return nombre.includes(q) || correo.includes(q) || tel.includes(q);
    });
  }, [clientes, busquedaCliente]);

  // ====== seleccionar cliente desde dropdown ======
  const seleccionarCliente = (c) => {
    const id = c.clienteID ?? c.ClienteID;
    const nombre = c.nombre ?? c.Nombre ?? "";

    setClienteSeleccionadoId(String(id));
    setClienteSeleccionadoNombre(nombre);
    setBusquedaCliente(nombre);
    setMostrarSugerencias(false);
  };

  // si escribe manualmente, des-selecciona hasta que elija uno
  const onChangeBusqueda = (val) => {
    setBusquedaCliente(val);
    setMostrarSugerencias(true);

    if (clienteSeleccionadoNombre && val !== clienteSeleccionadoNombre) {
      setClienteSeleccionadoId("");
      setClienteSeleccionadoNombre("");
    }
  };

  // ====== detalles ======
  const agregarServicio = () => {
    setServicios([
      ...servicios,
      { cantidad: "", descripcion: "", precioUnitario: "" },
    ]);
  };

  const actualizarServicio = (i, campo, valor) => {
    const nuevos = [...servicios];

    if (campo === "cantidad" || campo === "precioUnitario") {
      const limpio = String(valor).replace(/[^\d.]/g, "");
      nuevos[i][campo] = limpio;
    } else {
      nuevos[i][campo] = valor;
    }

    setServicios(nuevos);
  };

  const eliminarServicio = (i) => {
    setServicios(servicios.filter((_, idx) => idx !== i));
  };

  // ====== cálculos ======
  const subtotal = useMemo(() => {
    return servicios.reduce((acc, s) => {
      const cant = Number(s.cantidad) || 0;
      const precio = Number(s.precioUnitario) || 0;
      return acc + cant * precio;
    }, 0);
  }, [servicios]);

  const iva = useMemo(() => subtotal * 0.16, [subtotal]);
  const total = useMemo(() => subtotal + iva, [subtotal, iva]);

  // ====== crear cliente ======
  const abrirNuevoCliente = () => {
    setMostrarNuevoCliente(true);
    setNuevoCliente({ nombre: "", telefono: "", correo: "" });
  };

  const cerrarNuevoCliente = () => {
    setMostrarNuevoCliente(false);
    setNuevoCliente({ nombre: "", telefono: "", correo: "" });
  };

  const guardarNuevoCliente = async () => {
    const nombre = nuevoCliente.nombre.trim();
    const telefono = nuevoCliente.telefono.trim();
    const correo = nuevoCliente.correo.trim();

    if (!nombre) {
      alert("El nombre del cliente es obligatorio.");
      return;
    }

    try {
      const res = await api.post("/api/Clientes", { nombre, telefono, correo });

      const creado = res.data;
      const idCreado =
        creado?.clienteID ??
        creado?.ClienteID ??
        creado?.id ??
        creado?.Id;

      await cargarClientes();

      if (idCreado) {
        setClienteSeleccionadoId(String(idCreado));
        setClienteSeleccionadoNombre(nombre);
        setBusquedaCliente(nombre);
      }

      cerrarNuevoCliente();
      alert("Cliente creado ✅");
    } catch (err) {
      console.error("Error al crear cliente:", err);
      const msg =
        err?.response?.data ||
        "No se pudo crear el cliente (revisa la API).";
      alert(String(msg));
    }
  };

  // ====== guardar cotización ======
  const guardarCotizacion = async () => {
    if (!clienteSeleccionadoId) {
      alert("Selecciona un cliente.");
      return;
    }

    const detallesLimpios = servicios
      .map((s) => ({
        cantidad: Number(s.cantidad) || 0,
        descripcion: (s.descripcion || "").trim(),
        precioUnitario: Number(s.precioUnitario) || 0,
      }))
      .filter((d) => d.cantidad > 0 && d.precioUnitario >= 0 && d.descripcion);

    if (detallesLimpios.length === 0) {
      alert("Agrega al menos 1 servicio (cantidad, descripción y precio).");
      return;
    }

    try {
      const payload = {
        clienteID: Number(clienteSeleccionadoId),
        incluyeMaterial,
        incluyeManoObra,
        detalles: detallesLimpios,
      };

      const res = await api.post("/api/Cotizaciones", payload);

      const nuevaId =
        res.data?.cotizacionID ??
        res.data?.CotizacionID ??
        res.data?.id ??
        res.data?.Id;

      setCotizacionGuardadaId(nuevaId || null);

      alert("Cotización guardada ✅");
      await cargarCotizaciones();

      setServicios([{ cantidad: "", descripcion: "", precioUnitario: "" }]);
    } catch (error) {
      console.error("Error al guardar cotización:", error);
      const msg =
        error?.response?.data?.title ||
        error?.response?.data ||
        "Ocurrió un error al guardar.";
      alert(String(msg));
    }
  };

  // ====== exportar PDF ======
  const exportarPDF = () => {
    if (!cotizacionGuardadaId) {
      alert("Primero guarda la cotización para poder exportar el PDF.");
      return;
    }

    window.open(
      `${API_BASE}/api/Cotizaciones/${cotizacionGuardadaId}/pdf`,
      "_blank"
    );
  };

  // ====== actualizar estatus ======
  const actualizarEstatus = async (id, nuevoEstatus) => {
    try {
      await api.patch(`/api/Cotizaciones/${id}/estatus`, {
        estatus: nuevoEstatus.toLowerCase(),
      });

      setCotizaciones((prev) =>
        prev.map((c) =>
          (c.cotizacionID ?? c.CotizacionID) === id
            ? {
                ...c,
                estatus: nuevoEstatus,
                Estatus: nuevoEstatus,
              }
            : c
        )
      );
    } catch (error) {
      console.error("Error al actualizar estatus:", error);
      alert("No se pudo actualizar el estatus");
    }
  };

  // ====== filtro tabla ======
  const cotizacionesFiltradas = useMemo(() => {
    const q = filtroTabla.trim().toLowerCase();
    if (!q) return cotizaciones;

    return cotizaciones.filter((c) => {
      const cliente = (c.clienteNombre || c.ClienteNombre || "").toLowerCase();
      const estatus = (c.estatus || c.Estatus || "").toLowerCase();
      const id = String(c.cotizacionID || c.CotizacionID || "");
      return cliente.includes(q) || estatus.includes(q) || id.includes(q);
    });
  }, [cotizaciones, filtroTabla]);

  const year = new Date().getFullYear();

  return (
    <div className="page-wrapper">
      <div className="dashboard-layout">
        <aside className="sidebar">
          <div className="sidebar-title">RCI</div>
          <nav>
            <Link
              to="/dashboard"
              className={location.pathname === "/dashboard" ? "active" : ""}
            >
              Inicio
            </Link>
            <Link
              to="/cotizaciones"
              className={location.pathname.startsWith("/cotizaciones") ? "active" : ""}
            >
              Cotizaciones
            </Link>
            <Link
              to="/generadores"
              className={location.pathname.startsWith("/generadores") ? "active" : ""}
            >
              Generadores
            </Link>
            <Link
              to="/proyectos"
              className={location.pathname.startsWith("/proyectos") ? "active" : ""}
            >
              Proyectos
            </Link>
            <Link
              to="/clientes"
              className={location.pathname.startsWith("/clientes") ? "active" : ""}
            >
              Clientes
            </Link>
          </nav>
        </aside>

        <div className="main-area">
          <header className="header">Administrador de Proyectos</header>

          <main className="content">
            <h2>Crear Cotización</h2>

            <div className="cliente-top-row">
              <div className="cliente-autocomplete" ref={wrapperRef}>
                <label>Cliente</label>

                <input
                  type="text"
                  placeholder="Escribe para buscar cliente..."
                  value={busquedaCliente}
                  onChange={(e) => onChangeBusqueda(e.target.value)}
                  onFocus={() => setMostrarSugerencias(true)}
                />

                {mostrarSugerencias && busquedaCliente.trim() !== "" && (
                  <div className="cliente-sugerencias">
                    {clientesFiltrados.slice(0, 8).map((c) => {
                      const id = c.clienteID ?? c.ClienteID;
                      const nombre = c.nombre ?? c.Nombre;
                      const correo = c.correo ?? c.Correo;
                      const tel = c.telefono ?? c.Telefono;

                      return (
                        <div
                          key={id}
                          className="cliente-sugerencia-item"
                          onClick={() => seleccionarCliente(c)}
                        >
                          <div className="cliente-sug-nombre">{nombre}</div>
                          <div className="cliente-sug-sub">
                            {correo ? correo : "—"} {tel ? ` • ${tel}` : ""}
                          </div>
                        </div>
                      );
                    })}

                    {clientesFiltrados.length === 0 && (
                      <div className="cliente-sugerencia-empty">
                        Sin resultados. Crea un cliente nuevo.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button className="btn btn-nuevo-cliente" onClick={abrirNuevoCliente}>
                + Nuevo Cliente
              </button>
            </div>

            {mostrarNuevoCliente && (
              <div className="nuevo-cliente-panel">
                <h3>Nuevo Cliente</h3>

                <div className="nuevo-cliente-grid">
                  <div>
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={nuevoCliente.nombre}
                      onChange={(e) =>
                        setNuevoCliente({ ...nuevoCliente, nombre: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <label>RFC</label>
                    <input
                      type="text"
                      value={nuevoCliente.telefono}
                      onChange={(e) =>
                        setNuevoCliente({ ...nuevoCliente, telefono: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <label>Correo</label>
                    <input
                      type="email"
                      value={nuevoCliente.correo}
                      onChange={(e) =>
                        setNuevoCliente({ ...nuevoCliente, correo: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="acciones">
                  <button className="btn" onClick={guardarNuevoCliente}>
                    Guardar Cliente
                  </button>
                  <button className="btn btn-sec" onClick={cerrarNuevoCliente}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            <div className="incluye-row">
              <label className="check">
                <input
                  type="checkbox"
                  checked={incluyeMaterial}
                  onChange={(e) => setIncluyeMaterial(e.target.checked)}
                />
                Incluye Material
              </label>

              <label className="check">
                <input
                  type="checkbox"
                  checked={incluyeManoObra}
                  onChange={(e) => setIncluyeManoObra(e.target.checked)}
                />
                Incluye Mano de Obra
              </label>
            </div>

            <h3>Servicios</h3>
          
          <div className="table-container">
            <table className="tabla-servicios">
              <thead>
                <tr>
                  <th>Cantidad</th>
                  <th>Descripción</th>
                  <th>Precio Unitario</th>
                  <th>Importe</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {servicios.map((s, i) => (
                  <tr key={i}>
                    <td>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="Ej. 1"
                        value={s.cantidad}
                        onChange={(e) =>
                          actualizarServicio(i, "cantidad", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <input
                        type="text"
                        placeholder="Descripción..."
                        value={s.descripcion}
                        onChange={(e) =>
                          actualizarServicio(i, "descripcion", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="Ej. 150"
                        value={s.precioUnitario}
                        onChange={(e) =>
                          actualizarServicio(i, "precioUnitario", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      $
                      {(
                        (Number(s.cantidad) || 0) *
                        (Number(s.precioUnitario) || 0)
                      ).toFixed(2)}
                    </td>

                    <td>
                      <button
                        className="btn btn-eliminar"
                        onClick={() => eliminarServicio(i)}
                      >
                        ✖
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

            <button className="btn" onClick={agregarServicio}>
              + Agregar servicio
            </button>

            <div className="totales-box">
              <div>
                Subtotal: <b>${subtotal.toFixed(2)}</b>
              </div>
              <div>
                IVA (16%): <b>${iva.toFixed(2)}</b>
              </div>
              <div className="total-final">
                Total: <b>${total.toFixed(2)}</b>
              </div>
            </div>

            <div className="acciones">
              <button className="btn" onClick={guardarCotizacion}>
                Guardar Cotización
              </button>

              <button
                className="btn"
                onClick={exportarPDF}
                disabled={!cotizacionGuardadaId}
                title={!cotizacionGuardadaId ? "Guarda la cotización para habilitar" : ""}
              >
                Exportar en PDF
              </button>
            </div>

            <h3>Cotizaciones Realizadas</h3>

            <label>Filtrar:</label>
            <input
              type="text"
              value={filtroTabla}
              onChange={(e) => setFiltroTabla(e.target.value)}
              placeholder="Cliente / Estatus / ID..."
            />

          <div className="table-container">
            <table className="tabla-proyectos">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Total</th>
                  <th>Estatus</th>
                  <th>PDF</th>
                </tr>
              </thead>

              <tbody>
                {cotizacionesFiltradas.map((c) => {
                  const id = c.cotizacionID ?? c.CotizacionID;
                  const cliente = c.clienteNombre ?? c.ClienteNombre;
                  const fecha = c.fecha ?? c.Fecha;
                  const totalCot = c.total ?? c.Total;
                  const estatusRaw = c.estatus ?? c.Estatus ?? "Pendiente";

                  const estatusCapitalizado =
                    estatusRaw.charAt(0).toUpperCase() +
                    estatusRaw.slice(1).toLowerCase();

                  return (
                    <tr key={id}>
                      <td>{id}</td>
                      <td>{cliente}</td>
                      <td>{fecha ? new Date(fecha).toLocaleDateString() : ""}</td>
                      <td>${Number(totalCot || 0).toFixed(2)}</td>
                      <td>
                        <select
                          value={estatusCapitalizado}
                          onChange={(e) => actualizarEstatus(id, e.target.value)}
                          className="select-estatus"
                        >
                          <option value="Pendiente">Pendiente</option>
                          <option value="Aprobada">Aprobada</option>
                          <option value="Rechazada">Rechazada</option>
                        </select>
                      </td>
                      <td>
                        <button
                          className="btn-ver"
                          onClick={() =>
                            window.open(`${API_BASE}/api/Cotizaciones/${id}/pdf`, "_blank")
                          }
                        >
                          Ver PDF
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </main>
        </div>
      </div>

      <footer className="footer">© {year} RCI</footer>
    </div>
  );
}